import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  selectAILastSentMessage,
  selectAIRemainingRequests,
} from '@store/reducers/ai/selectors'
import { artBoardActions } from '@store/reducers/artboard'
import { saveProjectSaga } from '@store/reducers/manager/sagas'
import type { EventChannel, SagaIterator } from 'redux-saga'
import { call, put, select, take } from 'redux-saga/effects'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { AIExternalActions, AIInternalActions } from '../../actions'
import { ChatAgentHttpError } from '../../v2/chatAgentClient'
import type { ChatAgentStreamItem } from '../../v2/types'
import { createChatAgentChannel } from './streamChannel'

// The AbortController is deliberately kept out of Redux (non-serializable):
// only one chat turn can stream at a time (`takeLatest`), so a module-scoped
// reference is enough.
let activeAbortController: AbortController | null = null

/** Aborts the active chat stream, if any. Returns whether there was one. */
export const abortActiveChatStream = (): boolean => {
  if (!activeAbortController) {
    return false
  }

  activeAbortController.abort()
  activeAbortController = null

  return true
}

interface StreamOutcome {
  finished: boolean
  /** An `error` event was already surfaced to the user in this turn. */
  errorReported: boolean
  /**
   * Buffered `suggest_actions` payload. The actions message must be added
   * after the final text commit (ChatMessageItem renders it only when it is
   * the last message), so it is flushed on `done`/`stream_end`, not inline.
   */
  pendingActionButtons: 'main' | 'translate' | 'upgrade' | null
}

function* flushPendingActions(outcome: StreamOutcome): SagaIterator {
  if (!outcome.pendingActionButtons) {
    return
  }

  yield put(
    AIExternalActions.addMessage({
      message: {
        type: 'actions',
        buttons: outcome.pendingActionButtons,
      },
    })
  )

  outcome.pendingActionButtons = null
}

function* handleStreamItem(
  item: ChatAgentStreamItem,
  outcome: StreamOutcome,
  signal: AbortSignal
): SagaIterator {
  switch (item.type) {
    case 'message_start':
      yield put(AIInternalActions.setSessionId(item.sessionId))
      break

    case 'text_delta':
      yield put(AIInternalActions.appendStreamText(item.text))
      break

    case 'tool_start':
      yield put(AIInternalActions.setAgentStep(item.summary || item.name))
      break

    case 'agent_status':
      yield put(AIInternalActions.setAgentStatus(item.status))
      break

    case 'tool_result':
      yield put(AIInternalActions.setAgentStep(null))
      break

    case 'clarify':
      yield put(AIInternalActions.commitStreamedMessage())
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'clarify',
            text: item.question,
            options: item.options.map(({ id, label }) => ({
              id: id ?? label,
              label,
            })),
          },
        })
      )
      break

    case 'suggest_actions':
      // 'main' renders SEO + Translate, so it covers ['seo'] and both.
      // 'upgrade' (paid-plan CTA) wins if present. Buffered until the turn ends
      // (last suggest_actions wins): text can keep streaming after the tool
      // call, and the actions message only renders while it is the last one.
      if (item.actions.includes('upgrade')) {
        outcome.pendingActionButtons = 'upgrade'
      } else if (item.actions.includes('seo')) {
        outcome.pendingActionButtons = 'main'
      } else {
        outcome.pendingActionButtons = 'translate'
      }
      break

    case 'paywall':
      yield put(AIInternalActions.commitStreamedMessage())
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'paywall',
            text: item.message,
            requiredPlan: item.requiredPlan,
          },
        })
      )
      break

    case 'done': {
      yield put(AIInternalActions.commitStreamedMessage())
      yield call(flushPendingActions, outcome)

      if (typeof item.remainingRequests === 'number') {
        yield put(
          AIInternalActions.updateRemainingRequests(item.remainingRequests)
        )
      }

      if (item.status === 'error') {
        // The preceding `error` event (if any) already surfaced this.
        if (!outcome.errorReported) {
          sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_ERROR)
          yield put(AIInternalActions.setError('unknown'))
        }
      } else if (item.status !== 'stopped') {
        // ACTION means the project was actually changed. Only the server
        // knows that (successful non-dry-run write; the same flag debits a
        // request) — tool_result / toolCalls counts also fire on reads
        // (get_project) and on failed writes (paywall), so they must not
        // be used as a proxy.
        sendGtmEvent(
          item.mutated === true
            ? GtmTriggerName.AI_CHAT_RESPONSE_ACTION
            : GtmTriggerName.AI_CHAT_RESPONSE_NO_ACTION
        )
      }

      outcome.finished = true
      break
    }

    case 'error':
      // Keep the partial text: the user should not lose what already
      // streamed in. The server always pairs `error` with a subsequent
      // `done` (which carries remainingRequests), so keep reading the
      // stream instead of finishing here — `stream_end`/`stream_failure`
      // stay as terminal fallbacks if the server dies before `done`.
      yield put(AIInternalActions.commitStreamedMessage())
      sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_ERROR)
      yield put(AIInternalActions.setError('unknown'))
      outcome.errorReported = true
      break

    case 'stream_end':
      // The server closed the stream without a `done` event.
      yield put(AIInternalActions.commitStreamedMessage())
      yield call(flushPendingActions, outcome)
      if (!outcome.errorReported) {
        sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_ERROR)
        yield put(AIInternalActions.setError('network'))
      }
      outcome.finished = true
      break

    case 'stream_failure':
      if (!signal.aborted && !outcome.errorReported) {
        console.error('AI chat stream failed:', item.error)
        yield put(AIInternalActions.commitStreamedMessage())
        sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_ERROR)
        yield put(
          AIInternalActions.setError(
            // 409 turn_in_progress: another tab/turn is already streaming.
            item.error instanceof ChatAgentHttpError
              ? item.error.status === 409
                ? 'task_in_progress'
                : 'unknown'
              : 'network'
          )
        )
      }
      // Local abort (Stop button / new message): finish silently, the
      // partial text is committed by the `finally` cleanup.
      outcome.finished = true
      break

    default:
      // Unknown event types from newer server versions are ignored.
      break
  }
}

export function* sendMessageV2({
  payload: { message },
}: ReturnType<typeof AIExternalActions.sendMessage>): SagaIterator {
  const remainingRequests = yield select(selectAIRemainingRequests)
  if (remainingRequests === 0) {
    return
  }

  // "Try again" dispatches sendMessage({}) — resend the last user prompt.
  const lastSentMessage = yield select(selectAILastSentMessage)
  const content: string | null = message ?? lastSentMessage
  if (!content) {
    return
  }

  let projectId = yield select(migrationProjectSelector.getProjectId)
  if (!projectId) {
    // The chat agent requires a projectId on every /chat request, so an
    // unsaved project has to be persisted first (same as startChat).
    yield call(saveProjectSaga, { payload: { ignoreAuthenticated: true } })
    projectId = yield select(migrationProjectSelector.getProjectId)
  }

  if (!projectId) {
    yield put(AIInternalActions.setError('unknown'))
    return
  }

  abortActiveChatStream()
  const abortController = new AbortController()
  activeAbortController = abortController

  let channel: EventChannel<ChatAgentStreamItem> | null = null

  try {
    yield put(AIInternalActions.startStream())
    // Lock the canvas (frame + dim + disable editing) for the whole turn — from
    // the moment the assistant starts thinking, not only once it mutates — so it
    // is obvious work is in progress and the user can't race the sync (OCT-4826).
    yield put(artBoardActions.setAiGeneratingStatus({ status: true }))

    sendGtmEvent(GtmTriggerName.AI_CHAT_PROMPT_SEND)

    if (message) {
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'user',
            text: message,
          },
        })
      )
    }

    yield put(AIInternalActions.setLastSentMessage(content))

    channel = yield call(createChatAgentChannel, {
      projectId,
      content,
      signal: abortController.signal,
    })

    const outcome: StreamOutcome = {
      finished: false,
      errorReported: false,
      pendingActionButtons: null,
    }

    while (!outcome.finished) {
      const item: ChatAgentStreamItem = yield take(
        channel as EventChannel<ChatAgentStreamItem>
      )

      yield call(handleStreamItem, item, outcome, abortController.signal)
    }
  } finally {
    if (activeAbortController === abortController) {
      activeAbortController = null
    }

    // Stops the fetch when the saga is cancelled (takeLatest / Stop button);
    // a no-op when the stream has already ended.
    abortController.abort()
    channel?.close()

    // Keep whatever text managed to stream in (no-op when already
    // committed); drop the transient step indicator.
    yield put(AIInternalActions.commitStreamedMessage())
    yield put(artBoardActions.setAiGeneratingStatus({ status: false }))
    yield put(AIInternalActions.resetGeneration())
  }
}
